import * as real from '@/features/prequalification/localControl/endpoints/localPq'
import * as mock from '@/features/prequalification/localControl/endpoints/localPqMock'
import { searchLocalControlResources } from '@/features/prequalification/localControl/endpoints/resourceSearch'
import type { ResourceSetMember } from '@/features/prequalification/localControl/types'

/**
 * The single real/mock seam for local-control PQ. Hooks import `pqClient` and
 * never the underlying endpoint modules, so swapping a call from mock to real
 * is a one-line change here with no UI, hook, or test impact.
 *
 * `USE_LOCAL_PQ_MOCKS` routes the *swappable* endpoints to the in-memory store;
 * set it to `false` (current default) to run against the deployed backend. The
 * combined resource set CSV stays mocked regardless — that endpoint is deferred
 * (LC-196) and the UI has no trigger for it (see the endpoint table in LC-270).
 *
 * Resource search is *live*: it runs against the (deployed) Resources Manager
 * API via `searchLocalControlResources`, scoped to local-control resources —
 * it never depended on the PQ-specific `/local/resources` endpoint.
 */
const USE_LOCAL_PQ_MOCKS = false

/**
 * Live search, but in mock mode we also feed the results to the in-memory store
 * so a resource set later built from these (id-only) resources still resolves their
 * names. Against the real backend the search stays a plain pass-through — the
 * service resolves names itself.
 */
async function searchAndRememberResources(query: string): Promise<ResourceSetMember[]> {
  const resources = await searchLocalControlResources(query)
  mock.rememberResources(resources)
  return resources
}

export const pqClient = {
  // ── Live regardless of the PQ backend ──
  searchResources: USE_LOCAL_PQ_MOCKS ? searchAndRememberResources : searchLocalControlResources,

  // ── Permanently mocked: no backend exists ──
  // The combined resource set CSV (LC-196) is deferred and the UI removed its
  // trigger, so it has no consumer and no endpoint. Keep it on the mock store.
  downloadResourceSetCsv: mock.downloadResourceSetCsv,

  // ── Swappable: real contract exists in the spec, flip the flag when deployed ──
  listMarkets: USE_LOCAL_PQ_MOCKS ? mock.listMarkets : real.listLocalPqMarkets,
  listResourceSets: USE_LOCAL_PQ_MOCKS ? mock.listResourceSets : real.listLocalPqResourceSets,
  createResourceSet: USE_LOCAL_PQ_MOCKS ? mock.createResourceSet : real.createLocalPqResourceSet,
  getResourceSet: USE_LOCAL_PQ_MOCKS ? mock.getResourceSet : real.getLocalPqResourceSet,
  updateResourceSet: USE_LOCAL_PQ_MOCKS ? mock.updateResourceSet : real.updateLocalPqResourceSet,
  addResourceSetResources: USE_LOCAL_PQ_MOCKS ? mock.addResourceSetResources : real.addLocalPqResourceSetResources,
  removeResourceSetResource: USE_LOCAL_PQ_MOCKS
    ? mock.removeResourceSetResource
    : real.removeLocalPqResourceSetResource,
  listResourceSetRuns: USE_LOCAL_PQ_MOCKS ? mock.listResourceSetRuns : real.listLocalPqResourceSetRuns,
  runResourceSetTest: USE_LOCAL_PQ_MOCKS ? mock.runResourceSetTest : real.runLocalPqResourceSetTest,
  runResourceSetMarket: USE_LOCAL_PQ_MOCKS ? mock.runResourceSetMarket : real.runLocalPqResourceSetMarket,
  startRun: USE_LOCAL_PQ_MOCKS ? mock.startRun : real.startLocalPqRun,
  cancelRun: USE_LOCAL_PQ_MOCKS ? mock.cancelRun : real.cancelLocalPqRun,
  cancelResourceSet: USE_LOCAL_PQ_MOCKS ? mock.cancelResourceSet : real.cancelLocalPqResourceSet,
  deleteResourceSet: USE_LOCAL_PQ_MOCKS ? mock.deleteResourceSet : real.deleteLocalPqResourceSet,
  clearQueue: USE_LOCAL_PQ_MOCKS ? mock.clearQueue : real.clearLocalPqQueue,
  downloadRunCsv: USE_LOCAL_PQ_MOCKS ? mock.downloadRunCsv : real.downloadLocalPqRunCsv,
  downloadTestCsv: USE_LOCAL_PQ_MOCKS ? mock.downloadTestCsv : real.downloadLocalPqTestCsv,
}
